import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Pencil, Play, Trash2, Bookmark } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Badge } from '$lib/components/ui/Badge';
import { Button } from '$lib/components/ui/Button';
import { Input } from '$lib/components/ui/Input';
import { useSavedSearches } from '$lib/hooks/useSavedSearches';
import { deleteSavedSearch, updateSavedSearch } from '$lib/lib/savedSearches';
import { formatDateLong } from '$lib/lib/formatters';
import { SavedSearchSummaryLine } from '$lib/components/savedSearches/SavedSearchSummaryLine';

export function SavedSearchesPage() {
  const items = useSavedSearches();
  const navigate = useNavigate();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draftName, setDraftName] = useState('');
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const { t } = useTranslation();

  return (
    <div className="flex h-full min-h-0 flex-1 flex-col">
      <header className="flex h-11 shrink-0 items-center gap-3 border-b border-rule bg-surface-2 px-3">
        <h1 className="text-sm font-semibold text-fg">{t('savedSearches.title')}</h1>
        <span className="text-xs text-fg-muted">
          <span className="num text-fg">{items.length}</span> {t('nav.saved').toLowerCase()}
        </span>
        <Badge tone="info">{t('savedSearches.localOnlyBadge')}</Badge>
      </header>

      {items.length === 0 ? (
        <div className="flex flex-1 items-center justify-center p-12">
          <div className="max-w-md text-center">
            <Bookmark className="mx-auto text-fg-subtle" size={28} />
            <h2 className="mt-3 text-md font-semibold text-fg">{t('savedSearches.emptyTitle')}</h2>
            <p className="mt-1 text-xs text-fg-muted">
              <SavedSearchesEmptyBody />
            </p>
            <Button variant="primary" size="sm" className="mt-4" onClick={() => navigate('/units')}>
              {t('savedSearches.goToUnits')}
            </Button>
          </div>
        </div>
      ) : (
        <ul className="flex-1 overflow-y-auto" data-testid="saved-search-list">
          {items.map((item) => {
            const isEditing = editingId === item.id;
            return (
              <li
                key={item.id}
                data-testid="saved-search-item"
                className="row-line flex flex-col gap-1 px-4 py-3 hover:bg-surface-2"
              >
                <div className="flex items-start gap-3">
                  <Bookmark size={14} className="mt-0.5 shrink-0 text-fg-subtle" />
                  <div className="min-w-0 flex-1">
                    {isEditing ? (
                      <form
                        onSubmit={(e) => {
                          e.preventDefault();
                          updateSavedSearch({ id: item.id, name: draftName });
                          setEditingId(null);
                        }}
                        className="flex items-center gap-2"
                      >
                        <Input
                          autoFocus
                          value={draftName}
                          onChange={(e) => setDraftName(e.target.value)}
                          className="h-7"
                          aria-label={t('savedSearches.nameAriaLabel')}
                        />
                        <Button type="submit" variant="primary" size="sm">
                          {t('common.save')}
                        </Button>
                        <Button type="button" variant="ghost" size="sm" onClick={() => setEditingId(null)}>
                          {t('common.cancel')}
                        </Button>
                      </form>
                    ) : (
                      <button
                        type="button"
                        onClick={() => navigate(`/units${item.search}`)}
                        className="block w-full truncate text-left text-sm font-medium text-fg hover:text-accent"
                      >
                        {item.name}
                      </button>
                    )}
                    <div className="mt-0.5 truncate text-xs">
                      <SavedSearchSummaryLine summary={item.summary} />
                    </div>
                    <div className="mt-1 text-2xs text-fg-subtle">
                      {t('savedSearches.savedAt', { date: formatDateLong(item.createdAt) })}
                      {item.updatedAt !== item.createdAt && (
                        <> · {t('savedSearches.updatedAt', { date: formatDateLong(item.updatedAt) })}</>
                      )}
                    </div>
                  </div>
                  <div className="flex shrink-0 items-center gap-1">
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => navigate(`/units${item.search}`)}
                      data-testid="saved-search-restore"
                    >
                      <Play size={12} />
                      {t('savedSearches.restore')}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      aria-label={t('common.rename')}
                      title={t('common.rename')}
                      onClick={() => {
                        setEditingId(item.id);
                        setDraftName(item.name);
                      }}
                    >
                      <Pencil size={12} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      aria-label={t('common.delete')}
                      title={t('common.delete')}
                      onClick={() => setConfirmDeleteId(item.id)}
                      data-testid="saved-search-delete"
                    >
                      <Trash2 size={12} />
                    </Button>
                  </div>
                </div>
                {confirmDeleteId === item.id && (
                  <div
                    role="alert"
                    className="mt-1 ml-7 flex items-center gap-2 rounded-sm border border-danger/40 bg-danger/10 px-2 py-1 text-2xs text-danger"
                  >
                    <span className="flex-1">{t('savedSearches.deletePrompt', { name: item.name })}</span>
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={() => {
                        deleteSavedSearch(item.id);
                        setConfirmDeleteId(null);
                      }}
                      data-testid="saved-search-delete-confirm"
                    >
                      {t('common.delete')}
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => setConfirmDeleteId(null)}>
                      {t('common.cancel')}
                    </Button>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function SavedSearchesEmptyBody() {
  const { t } = useTranslation();
  const template = t('savedSearches.emptyBody');
  const open = '<bold>';
  const close = '</bold>';
  const oi = template.indexOf(open);
  const ci = template.indexOf(close);
  if (oi === -1 || ci === -1) return <>{template}</>;
  return (
    <>
      {template.slice(0, oi)}
      <span className="font-medium text-fg">{template.slice(oi + open.length, ci)}</span>
      {template.slice(ci + close.length)}
    </>
  );
}
